import {action, Action, thunk, Thunk} from 'easy-peasy';
import {LastPeriodicCheck, Machine, MachineActiveTabs, OperatingUnitTypePayload, PeriodicCheckPayload, Project, SearchMachine, SearchMachinePayload, SearchMachineResponse} from '~/types';
import {StoreModel} from "~/store";
import { API } from '~/app/routes';
import { machineRemote } from '~/data/remote/MachineRemoteDataSource';
import {CommonModel, getCommonModel} from "~/store/commonStore";
import { machineRepository } from '~/data/repositories';
import { localStorageGet, localStorageSet } from '~/app/helpers/localStorage';
import { MachineTabs } from '~/app/data/statuses/machine';
import { checkPermission } from '~/accessControl/helpers';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import moment, { Moment } from 'moment';
import { applyPeriodicCheckToMachineJson } from '~/periodicCheck/offlinePeriodicCheck';
import { OperatingTypes } from '~/app/data/statuses/operatingData';
import { connectivityService } from '~/data/connectivity/ConnectivityService';

export interface OperatingTimePayload {
    machineId: Machine['id'],
    operatingTime: Machine['operatingTime'] | Machine['operatingCount'],
    type: OperatingUnitTypePayload,
    sync?: number
}

/** After periodic / acceptance checklist submit — keep Registration row in sync when API omits linked `lastDailyCheck`. */
export interface ApplyLocalLastPeriodicCheckPayload {
    machineId: Machine['id']
    inspectionType: Machine['inspectionType']
    lastCheck: LastPeriodicCheck
}

export interface ApplyLocalPeriodicCheckChecksPayload {
    machineId: Machine['id']
    inspectionType: Machine['inspectionType']
    checks: PeriodicCheckPayload[]
    lastCheck: LastPeriodicCheck
    projectId?: Project['id']
}

export interface ClearPeriodicCheckPendingSyncPayload {
    machineId: Machine['id']
    inspectionType: Machine['inspectionType']
}

export interface ApplyLocalOperatingDataPayload {
    machineId: Machine['id']
    operatingTime: OperatingTimePayload['operatingTime']
    type: OperatingUnitTypePayload
}

export interface ClearOperatingDataPendingSyncPayload {
    machineId: Machine['id']
    type: OperatingUnitTypePayload
}

export interface MachineModel extends CommonModel<Machine> {
    tabs: MachineActiveTabs['activeTab'][]
    defaultTab: MachineActiveTabs['activeTab']
    setDefaultTab: Action<MachineModel, MachineActiveTabs['activeTab']>
    activeTab: MachineActiveTabs['activeTab']
    setActiveTab: Action<MachineModel, MachineActiveTabs['activeTab']>
    
    dataNotAvailableSinceDate: Moment | null
    setDataNotAvailableSinceDate: Action<MachineModel, Moment | null>
    
    data: Machine
    fetchMachine: Thunk<MachineModel, Machine['id'], any, StoreModel, Promise<void>>
    fetchedMachine: Action<MachineModel, Machine>
    applyLocalLastPeriodicCheck: Action<MachineModel, ApplyLocalLastPeriodicCheckPayload>
    applyLocalPeriodicCheckChecks: Action<MachineModel, ApplyLocalPeriodicCheckChecksPayload>
    clearPeriodicCheckPendingSync: Action<MachineModel, ClearPeriodicCheckPendingSyncPayload>
    applyLocalOperatingData: Action<MachineModel, ApplyLocalOperatingDataPayload>
    clearOperatingDataPendingSync: Action<MachineModel, ClearOperatingDataPendingSyncPayload>
    registerOperatingTimeOrCount: Thunk<MachineModel, OperatingTimePayload, any, StoreModel, Promise<void>>
    
    fetchMachineDashboard: Thunk<MachineModel, Machine['id'], any, StoreModel, Promise<void>>
    fetchedMachineDashboard: Action<MachineModel, Machine>
    initActiveTab: Thunk<MachineModel, void, any, StoreModel, void>
    resetActiveTab: Thunk<MachineModel, void, any, StoreModel, void>

    displayTypeInIssues: boolean
    setDisplayTypeInIssues: Action<MachineModel, boolean>

    isIssueTypeRequired: boolean
    setIsIssueTypeRequired: Action<MachineModel, boolean>

    searchMachines: SearchMachine[]
    searchMachinesTotal: number,
    fetchingSearchMachines: boolean
    setFetchingSearchMachines: Action<MachineModel, boolean>
    fetchSearchMachine: Thunk<MachineModel, SearchMachinePayload, any, StoreModel, Promise<SearchMachine[]>>
    fetchedSearchMachines: Action<MachineModel, SearchMachineResponse>
}

export const machineModel: MachineModel = {
    ...getCommonModel<Machine>(API.machines),
    tabs: [MachineTabs.DASHBOARD, MachineTabs.REGISTRATION],
    defaultTab: MachineTabs.EMPTYTAB,
    setDefaultTab: action((state, payload) => {
        state.defaultTab = payload === MachineTabs.DASHBOARD || payload === MachineTabs.REGISTRATION ? payload : MachineTabs.EMPTYTAB;
    }),
    activeTab: localStorageGet('lastVisitedTab') as MachineTabs || MachineTabs.EMPTYTAB,
    setActiveTab: action((state, payload) => {
        const defaultTab = payload === MachineTabs.DASHBOARD || payload === MachineTabs.REGISTRATION ? payload : MachineTabs.EMPTYTAB;
        state.activeTab = defaultTab;
        defaultTab.length && localStorageSet('lastVisitedTab', defaultTab);
    }),
    dataNotAvailableSinceDate: null,
    setDataNotAvailableSinceDate: action((state, payload) => {
        state.dataNotAvailableSinceDate = payload;
    }),
    data: {
        id: '',
        name: '',
        intern: '',
        regNo: '',
        checks: [],
        acceptanceInspectionChecks: [],
        inspectionType: 0,
        issuesCount: [],
        issues: [],
        services: [],
        certification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        adrCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        liftControlCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        electricalControlCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        psvCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        airTank1YearCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        extinguishingEquipmentCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        airTank2YearCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        electricEngineCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        ndtCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        airTank10YearCertification: {
            type: '',
            lastDate: '',
            nextDate: '',
            isApproved: false
        },
        idleTimeHistoryData: [],
        fuelConsumptionHistoryData: [],
        inspectionData: [],
        idleHoursThresholdValues: {
            lowThresholdEnd: 0,
            middleThresholdEnd: 0,
            highThresholdEnd: 0
        },
        inspectionDataOfLast10Weeks: [],
        hasAccessToCompany: false,
        trainings: []
    },
    fetchMachine: thunk(async (actions, payload) => {
        actions.setFetching(true);
        try {
            const data = await machineRepository.getMachine(payload);
            data && actions.fetchedMachine(data);
        } finally {
            actions.setFetching(false);
        }
    }),
    fetchedMachine: action((state, payload) => {
        state.data = payload;
    }),
    applyLocalLastPeriodicCheck: action((state, payload) => {
        const ACCEPTANCE_INSPECTION = 2;
        const field = payload.inspectionType === ACCEPTANCE_INSPECTION
            ? 'lastAcceptanceInspection'
            : 'lastDailyCheck';
        const targetId = String(payload.machineId);
        if (String(state.data.id) === targetId) {
            state.data[field] = payload.lastCheck;
            return;
        }
        const linked = state.data.linkedMachines?.find(l => String(l.id) === targetId);
        if (linked) {
            linked[field] = payload.lastCheck;
        }
    }),
    applyLocalPeriodicCheckChecks: action((state, payload) => {
        const applied = applyPeriodicCheckToMachineJson(
            state.data,
            payload.machineId,
            Number(payload.inspectionType),
            payload.lastCheck,
            payload.checks,
            payload.projectId,
        );
        if (applied.changed) {
            state.data = applied.machine;
        }
    }),
    clearPeriodicCheckPendingSync: action((state, payload) => {
        const ACCEPTANCE_INSPECTION = 2;
        const field = payload.inspectionType === ACCEPTANCE_INSPECTION
            ? 'lastAcceptanceInspection'
            : 'lastDailyCheck';
        const targetId = String(payload.machineId);
        const clear = (slice: Machine) => {
            const current = slice[field];
            if (!current?.pendingSync) {
                return;
            }
            slice[field] = { ...current, pendingSync: false };
        };
        if (String(state.data.id) === targetId) {
            clear(state.data);
            return;
        }
        const linked = state.data.linkedMachines?.find(item => String(item.id) === targetId);
        if (linked) {
            clear(linked);
        }
    }),
    applyLocalOperatingData: action((state, payload) => {
        const today = moment().format('YYYY-MM-DD');
        const apply = (slice: Machine) => {
            if (Number(payload.type) === OperatingTypes.OPERATING_COUNT) {
                slice.operatingCount = payload.operatingTime as Machine['operatingCount'];
                slice.lastOperatingCountDate = today;
                slice.operatingCountPendingSync = true;
                return;
            }
            slice.operatingTime = payload.operatingTime as Machine['operatingTime'];
            slice.lastOperatingTimeDate = today;
            slice.operatingTimePendingSync = true;
        };
        const targetId = String(payload.machineId);
        if (String(state.data.id) === targetId) {
            apply(state.data);
            return;
        }
        const linked = state.data.linkedMachines?.find(item => String(item.id) === targetId);
        if (linked) {
            apply(linked);
        }
    }),
    clearOperatingDataPendingSync: action((state, payload) => {
        const clear = (slice: Machine) => {
            if (Number(payload.type) === OperatingTypes.OPERATING_COUNT) {
                if (!slice.operatingCountPendingSync) {
                    return;
                }
                slice.operatingCountPendingSync = false;
                return;
            }
            if (!slice.operatingTimePendingSync) {
                return;
            }
            slice.operatingTimePendingSync = false;
        };
        const targetId = String(payload.machineId);
        if (String(state.data.id) === targetId) {
            clear(state.data);
            return;
        }
        const linked = state.data.linkedMachines?.find(item => String(item.id) === targetId);
        if (linked) {
            clear(linked);
        }
    }),
    registerOperatingTimeOrCount: thunk(async (actions, payload) => {
        actions.setUpdating(true);
        try {
            await machineRepository.registerOperatingTime(payload);
            if (!payload.sync) {
                if (!connectivityService.isOnline()) {
                    actions.applyLocalOperatingData(payload);
                }
                await actions.fetchMachine(payload.machineId);
            }
        } finally {
            actions.setUpdating(false);
        }
    }),
    fetchMachineDashboard: thunk(async (actions, payload) => {
        actions.setFetching(true);
        try {
            const data = await machineRepository.getDashboard(payload);
            data && actions.fetchedMachineDashboard(data);
        } finally {
            actions.setFetching(false);
        }
    }),
    fetchedMachineDashboard: action((state, payload) => {
        state.data = {
            ...state.data,
            ...payload
        }
    }),
    initActiveTab: thunk((actions, payload, {getStoreState}) => {
        const userPermissions = getStoreState().user.permissions;
        const machineDashboardPermission = checkPermission(userPermissions, PERMISSION_GROUPS.machineDashboard);
        const machineRegistrationPermission = checkPermission(userPermissions, PERMISSION_GROUPS.machineRegistration);

        const defaultTab = getStoreState().machine.defaultTab;
        const activeTab = getStoreState().machine.activeTab;

        if(machineDashboardPermission && activeTab === MachineTabs.DASHBOARD) {
            actions.setActiveTab(MachineTabs.DASHBOARD);
        } else if(machineRegistrationPermission && activeTab === MachineTabs.REGISTRATION) {
            actions.setActiveTab(MachineTabs.REGISTRATION);
        } else if(machineDashboardPermission && defaultTab === MachineTabs.DASHBOARD) {
            actions.setActiveTab(MachineTabs.DASHBOARD);
        } else if(machineRegistrationPermission && defaultTab === MachineTabs.REGISTRATION) {
            actions.setActiveTab(MachineTabs.REGISTRATION);
        } else if(machineDashboardPermission || machineRegistrationPermission) {
            actions.setActiveTab(machineDashboardPermission ? MachineTabs.DASHBOARD : MachineTabs.REGISTRATION);
        }
    }),
    resetActiveTab: thunk((actions, payload, {getStoreState}) => {
        const userPermissions = getStoreState().user.permissions;
        const machineDashboardPermission = checkPermission(userPermissions, PERMISSION_GROUPS.machineDashboard);
        const machineRegistrationPermission = checkPermission(userPermissions, PERMISSION_GROUPS.machineRegistration);
        const defaultTab = getStoreState().machine.defaultTab;

        if(machineDashboardPermission && defaultTab === MachineTabs.DASHBOARD) {
            actions.setActiveTab(MachineTabs.DASHBOARD);
        } else if(machineRegistrationPermission && defaultTab === MachineTabs.REGISTRATION) {
            actions.setActiveTab(MachineTabs.REGISTRATION);
        }
    }),
    displayTypeInIssues: false,
    setDisplayTypeInIssues: action((state, payload) => {
        state.displayTypeInIssues = payload;
    }),

    isIssueTypeRequired: false,
    setIsIssueTypeRequired: action((state, payload) => {
        state.isIssueTypeRequired = payload;
    }),

    searchMachines: [],
    searchMachinesTotal: 0,
    fetchingSearchMachines: false,
    setFetchingSearchMachines: action((state, payload) => {
        state.fetchingSearchMachines = payload;
    }),
    fetchSearchMachine: thunk((actions, payload) => {
        actions.setFetchingSearchMachines(true);
    
        return machineRemote.searchMachines(payload)
          .then(data => {
            actions.fetchedSearchMachines(data);
            return data.machines;
          })
          .finally(() => {
            actions.setFetchingSearchMachines(false);
          });
    }),
    fetchedSearchMachines: action((state, payload) => {
        if (payload.page > 1) {
            state.searchMachines = [...state.searchMachines, ...payload.machines];
        } else {
            state.searchMachines = payload.machines;
        }
        state.searchMachinesTotal = payload.total;
    }),

};
